<template>
  <div>
    <div class="admin-toolbar">
      <p class="admin-subtitle">
        {{ newspapers.length }} {{ newspapers.length === 1 ? 'issue' : 'issues' }} · paid issues need a purchase before download
      </p>
      <button @click="openModal('add')" class="btn-primary">
        <i class="fa-solid fa-plus"></i> Add Newspaper
      </button>
    </div>

    <!-- Newspapers Table -->
    <div v-if="loadingNewspapers" class="admin-empty">Loading newspapers...</div>
    <div v-else-if="listError" class="admin-empty text-red-600">{{ listError }}</div>
    <div v-else-if="newspapers.length === 0" class="admin-empty">
      <i class="fa-solid fa-newspaper text-2xl text-gray-300"></i>
      No newspapers found.
    </div>
    <div v-else class="admin-table-wrap">
      <table class="admin-table">
        <thead>
          <tr>
            <th>Issue</th>
            <th>Description</th>
            <th>Access</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="newspaper in newspapers" :key="newspaper.id">
            <td>
              <div class="flex items-center gap-3">
                <img
                  :src="newspaper.thumbnail_path"
                  :alt="newspaper.title"
                  class="h-14 w-11 flex-shrink-0 rounded border border-gray-200 bg-gray-50 object-cover"
                />
                <span class="font-medium text-gray-900">{{ newspaper.title }}</span>
              </div>
            </td>
            <td class="max-w-sm">{{ newspaper.description || 'No description' }}</td>
            <!-- Access badge -->
            <td>
              <span
                :class="newspaper.is_paid ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'"
                class="badge"
              >
                <i :class="['fa-solid', newspaper.is_paid ? 'fa-lock' : 'fa-lock-open']"></i>
                {{ newspaper.is_paid ? 'Paid' : 'Free' }}
              </span>
            </td>
            <td>
              <div class="flex flex-wrap justify-end gap-2">
                <button @click="viewPdf(newspaper)" class="btn-ghost btn-sm">
                  <i class="fa-regular fa-file-pdf"></i> View PDF
                </button>
                <!-- Free / Paid toggle -->
                <button @click="togglePaid(newspaper)" class="btn-ghost btn-sm">
                  {{ newspaper.is_paid ? 'Make Free' : 'Make Paid' }}
                </button>
                <button @click="openModal('update', newspaper)" class="btn-cream btn-sm">
                  <i class="fa-solid fa-pen"></i> Edit
                </button>
                <button @click="confirmDelete(newspaper.id)" class="btn-danger btn-sm">Delete</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal for Upload/Update Form -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
        <div class="modal-panel">
          <h2 class="mb-6 font-serif text-2xl font-bold text-gray-900">
            {{ modalMode === "add" ? "Upload Monthly Newspaper" : "Update Newspaper" }}
          </h2>

          <form
            @submit.prevent="modalMode === 'add' ? uploadFiles() : updateNewspaper()"
            class="space-y-5"
          >
            <!-- Title -->
            <div>
              <label class="form-label">Title</label>
              <input
                type="text"
                v-model="title"
                placeholder="e.g. August Edition"
                required
                class="form-input"
              />
            </div>

            <!-- Description -->
            <div>
              <label class="form-label">Description</label>
              <textarea
                v-model="description"
                placeholder="Enter a short description"
                rows="3"
                class="form-input resize-none"
              ></textarea>
            </div>

            <div>
              <label for="pdfInput" class="form-label">
                PDF File {{ modalMode === 'add' ? '(required)' : '(optional — leave empty to keep the current PDF)' }}
              </label>
              <input
                id="pdfInput"
                ref="pdfInput"
                type="file"
                @change="onPDFChange"
                accept="application/pdf"
                :required="modalMode === 'add'"
                class="file-input"
              />
            </div>

            <!-- Image Upload -->
            <div>
              <label for="imageInput" class="form-label">
                Cover Image {{ modalMode === 'add' ? '' : '(optional — leave empty to keep the current cover)' }}
              </label>
              <input
                id="imageInput"
                ref="imageInput"
                type="file"
                @change="onImageChange"
                accept="image/*"
                class="file-input"
              />
              <div v-if="imagePreview" class="mt-3 flex items-center gap-3">
                <img
                  :src="imagePreview"
                  alt="Cover Preview"
                  class="h-28 w-20 rounded border border-gray-200 object-cover shadow-sm"
                />
                <p class="text-xs text-gray-500">{{ imageFile ? 'New cover' : 'Current cover' }}</p>
              </div>
            </div>

            <!-- is_paid Toggle -->
            <div class="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 p-3">
              <div>
                <p class="font-medium text-gray-800">Paid Issue</p>
                <p class="text-xs text-gray-500">Turn off to make this newspaper free to download</p>
              </div>
              <label class="relative inline-flex cursor-pointer items-center">
                <input type="checkbox" v-model="is_paid" class="peer sr-only" />
                <div class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-emerald-500 peer-checked:after:translate-x-5 peer-focus:ring-2 peer-focus:ring-emerald-400"></div>
                <span class="ml-3 w-10 text-sm font-semibold" :class="is_paid ? 'text-emerald-600' : 'text-gray-500'">
                  {{ is_paid ? 'Paid' : 'Free' }}
                </span>
              </label>
            </div>

            <!-- Status Messages -->
            <p v-if="error" class="rounded-md border border-red-100 bg-red-50 p-3 text-sm text-red-700">{{ error }}</p>

            <div class="flex gap-3 pt-1">
              <button type="button" @click="closeModal" class="btn-ghost flex-1">Cancel</button>
              <button type="submit" :disabled="loading" class="btn-primary flex-1">
                <template v-if="!loading">{{ modalMode === "add" ? "Upload" : "Save Changes" }}</template>
                <template v-else>{{ modalMode === "add" ? "Uploading..." : "Saving..." }}</template>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import api, { errorMessage, openPdf } from '@/api';

export default {
  name: "UploadNewspaper",
  data() {
    return {
      title: "",
      description: "",
      is_paid: true,           // ← default NEW uploads to paid
      pdfFile: null,
      imageFile: null,
      imagePreview: null,
      loading: false,
      error: "",
      listError: "",
      newspapers: [],
      loadingNewspapers: false,
      showModal: false,
      modalMode: "add",
      selectedNewspaperId: null,
    };
  },
  methods: {
    onPDFChange(e) {
      this.pdfFile = e.target.files[0] || null;
    },
    onImageChange(e) {
      this.imageFile = e.target.files[0] || null;
      if (this.imageFile) this.imagePreview = URL.createObjectURL(this.imageFile);
    },

    buildFormData() {
      const formData = new FormData();
      formData.append("title", this.title);
      formData.append("description", this.description);
      formData.append("is_paid", this.is_paid ? 1 : 0);
      if (this.pdfFile) formData.append("pdfFile", this.pdfFile);
      if (this.imageFile) formData.append("imageFile", this.imageFile);
      return formData;
    },

    /** ADD NEW NEWSPAPER (POST) */
    async uploadFiles() {
      if (!this.pdfFile) {
        this.error = "Please select a PDF file.";
        return;
      }

      this.loading = true;
      this.error = "";

      try {
        const res = await api.post('/upload-pdf', this.buildFormData());
        await this.fetchNewspapers();
        this.closeModal();
        Swal.fire({ icon: 'success', title: 'Uploaded', text: res.data.message || 'Newspaper added successfully!', timer: 1800, showConfirmButton: false });
      } catch (err) {
        this.error = errorMessage(err, "Upload failed.");
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /** UPDATE NEWSPAPER (PUT) — PDF and cover are optional */
    async updateNewspaper() {
      this.loading = true;
      this.error = "";

      try {
        const res = await api.put(`/newspapers/${this.selectedNewspaperId}`, this.buildFormData());
        await this.fetchNewspapers();
        this.closeModal();
        Swal.fire({ icon: 'success', title: 'Saved', text: res.data.message || 'Newspaper updated successfully!', timer: 1800, showConfirmButton: false });
      } catch (err) {
        this.error = errorMessage(err, "Update failed.");
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /** DELETE */
    async deleteNewspaper(id) {
      try {
        await api.delete(`/newspapers/${id}`);
        await this.fetchNewspapers();
        Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Newspaper deleted successfully.', timer: 1600, showConfirmButton: false });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to delete newspaper.'), confirmButtonColor: '#BF3131' });
      }
    },

    async confirmDelete(id) {
      const result = await Swal.fire({
        title: 'Delete Newspaper?',
        text: "The PDF and cover image are deleted too. You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DC2626',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await this.deleteNewspaper(id);
      }
    },

    async viewPdf(newspaper) {
      try {
        await openPdf('newspaper', newspaper.id);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Could not open the PDF.'), confirmButtonColor: '#BF3131' });
      }
    },

    /** OPEN & CLOSE MODAL */
    openModal(mode, newspaper = null) {
      this.modalMode = mode;
      this.error = '';
      if (mode === 'update' && newspaper) {
        this.title = newspaper.title;
        this.description = newspaper.description || '';
        this.is_paid = !!newspaper.is_paid;
        this.selectedNewspaperId = newspaper.id;
        this.imagePreview = newspaper.thumbnail_path || null;
      } else {
        this.title = '';
        this.description = '';
        this.is_paid = true; // default new = paid
        this.selectedNewspaperId = null;
        this.imagePreview = null;
      }
      this.pdfFile = null;
      this.imageFile = null;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },

    /** FETCH all newspapers */
    async fetchNewspapers() {
      this.loadingNewspapers = true;
      this.listError = '';
      try {
        const res = await api.get('/newspapers');
        this.newspapers = res.data;
      } catch (err) {
        console.error('Failed to fetch newspapers:', err);
        this.listError = errorMessage(err, 'Failed to load newspapers.');
      } finally {
        this.loadingNewspapers = false;
      }
    },

    /** TOGGLE FREE / PAID */
    async togglePaid(newspaper) {
      const action = newspaper.is_paid ? 'make free' : 'make paid';
      const result = await Swal.fire({
        title: newspaper.is_paid ? 'Make Free?' : 'Make Paid?',
        text: `Are you sure you want to ${action} "${newspaper.title}"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#D84040',
        cancelButtonColor: '#6B7280',
        confirmButtonText: `Yes, ${action}!`
      });
      if (!result.isConfirmed) return;

      try {
        const res = await api.put(`/admin/newspapers/${newspaper.id}/toggle-paid`);
        const listed = this.newspapers.find(n => n.id === newspaper.id);
        if (listed) listed.is_paid = res.data.is_paid;

        Swal.fire({
          icon: 'success',
          title: res.data.is_paid ? 'Now Paid' : 'Now Free',
          text: res.data.message,
          timer: 1800,
          showConfirmButton: false
        });
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: errorMessage(err, 'Failed to toggle.'), confirmButtonColor: '#BF3131' });
      }
    },
  },
  mounted() {
    this.fetchNewspapers();
  },
};
</script>
